import type { LogFn } from '../../log.js';
import type { CloseableBridgeState } from '../close-bridge-connection.js';
import type { MainBridgeReconnectState } from '../reconnect/index.js';
import {
  armReconnectDelayTimer,
  applyTieredReconnectPlanAndLog,
  PREVIEW_TUNNEL_DISCONNECT_LEAD_IN,
  PREVIEW_TUNNEL_SERVICE_LABEL,
} from '../reconnect/index.js';
import type { AttachFirehoseContext } from './attach-firehose-context.js';

export function createFirehoseReconnectScheduler(
  ctx: AttachFirehoseContext,
  reconnect: () => void,
): {
  clearFirehoseReconnectTimer: () => void;
  scheduleFirehoseRetryAfterDrop: (closeMeta?: { code: number; reason: string }) => void;
} {
  const { state, logFn } = ctx;

  function clearFirehoseReconnectTimer(): void {
    if (state.firehoseReconnectTimeout != null) {
      clearTimeout(state.firehoseReconnectTimeout);
      state.firehoseReconnectTimeout = null;
    }
  }

  function scheduleFirehoseRetryAfterDrop(closeMeta?: { code: number; reason: string }): void {
    if (state.closedByUser) return;
    const meta = closeMeta ?? state.lastFirehoseReconnectCloseMeta ?? undefined;
    const delay = applyTieredReconnectPlanAndLog(
      state.firehoseOutage,
      logFn,
      PREVIEW_TUNNEL_SERVICE_LABEL,
      PREVIEW_TUNNEL_DISCONNECT_LEAD_IN,
      meta?.code,
      meta?.reason,
    );
    armReconnectDelayTimer({
      delayMs: delay,
      bumpAttempt: () => {
        state.firehoseReconnectAttempt += 1;
      },
      clearTimer: clearFirehoseReconnectTimer,
      setTimer: (id) => {
        state.firehoseReconnectTimeout = id;
      },
      shouldAbortBeforeRun: () => state.closedByUser,
      run: reconnect,
      reschedule: () => {
        scheduleFirehoseRetryAfterDrop();
      },
    });
  }

  return { clearFirehoseReconnectTimer, scheduleFirehoseRetryAfterDrop };
}

export function logInitialFirehoseConnect(
  state: MainBridgeReconnectState & CloseableBridgeState,
  logFn: LogFn,
): void {
  if (state.firehoseReconnectAttempt !== 0) return;
  try {
    logFn('Connecting to preview tunnel (local HTTP proxy and dev logs)…');
  } catch {
    /* ignore */
  }
}

export function replaceFirehoseHandle(
  state: CloseableBridgeState & MainBridgeReconnectState,
): number {
  state.firehoseGeneration += 1;
  const myGen = state.firehoseGeneration;
  if (state.firehoseHandle) {
    state.firehoseHandle.close();
    state.firehoseHandle = null;
  }
  return myGen;
}
